import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from '@/components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { Plus, Pencil, Trash2, ExternalLink, Loader2, RefreshCw } from 'lucide-react';
import { getUrls, createUrl, updateUrl, deleteUrl } from '@/services/api';
import type { Url, CreateUrlInput, UpdateUrlInput } from '@/types/url';
import { useNetwork } from '@/contexts/NetworkContext';

export default function Urls() {
    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [selectedUrl, setSelectedUrl] = useState<Url | null>(null);
    const [formData, setFormData] = useState<CreateUrlInput>({
        network: '',
        url: '',
        inventory_type: 'Display',
        brand_type: 'Com marca',
    });

    const { toast } = useToast();
    const { networks, format: formatConta } = useNetwork();
    const queryClient = useQueryClient();

    // Query para listar URLs
    const { data: urls, isLoading, error } = useQuery({
        queryKey: ['urls'],
        queryFn: getUrls,
    });

    // Mutation para criar URL
    const createUrlMutation = useMutation({
        mutationFn: createUrl,
        onSuccess: () => {
            toast({
                title: 'URL criada com sucesso!',
                description: 'A URL foi criada e está aguardando processamento.',
            });
            queryClient.invalidateQueries({ queryKey: ['urls'] });
            setIsCreateDialogOpen(false);
            resetForm();
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao criar URL',
                description: error.response?.data?.detail || 'Ocorreu um erro ao criar a URL.',
            });
        },
    });

    // Mutation para atualizar URL
    const updateUrlMutation = useMutation({
        mutationFn: ({ urlId, data }: { urlId: number; data: UpdateUrlInput }) =>
            updateUrl(urlId, data),
        onSuccess: () => {
            toast({
                title: 'URL atualizada com sucesso!',
                description: 'A URL foi atualizada.',
            });
            queryClient.invalidateQueries({ queryKey: ['urls'] });
            setIsEditDialogOpen(false);
            setSelectedUrl(null);
            resetForm();
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao atualizar URL',
                description: error.response?.data?.detail || 'Ocorreu um erro ao atualizar a URL.',
            });
        },
    });

    // Mutation para deletar URL
    const deleteUrlMutation = useMutation({
        mutationFn: deleteUrl,
        onSuccess: () => {
            toast({
                title: 'URL deletada com sucesso!',
                description: 'A URL foi removida do sistema.',
            });
            queryClient.invalidateQueries({ queryKey: ['urls'] });
            setIsDeleteDialogOpen(false);
            setSelectedUrl(null);
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao deletar URL',
                description: error.response?.data?.detail || 'Ocorreu um erro ao deletar a URL.',
            });
        },
    });

    // Mutation para sync
    const syncMutation = useMutation({
        mutationFn: async () => {
            const response = await fetch(`${import.meta.env.VITE_GAM_API_URL}/sync`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
                    'Content-Type': 'application/json',
                },
            });
            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.detail || 'Erro ao sincronizar');
            }
            return response.json();
        },
        onSuccess: () => {
            toast({
                title: 'Sincronização concluída!',
                description: 'As URLs foram sincronizadas com sucesso.',
            });
            queryClient.invalidateQueries({ queryKey: ['urls'] });
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao sincronizar',
                description: error.message || 'Ocorreu um erro ao sincronizar as URLs.',
            });
        },
    });

    const handleSync = () => {
        syncMutation.mutate();
    };

    const resetForm = () => {
        setFormData({
            network: '',
            url: '',
            inventory_type: 'Display',
            brand_type: 'Com marca',
        });
    };

    // Função para validar formato de URL (sem https://)
    const validateUrlFormat = (url: string): boolean => {
        // Remove espaços
        const trimmedUrl = url.trim();
        
        // Não deve começar com http:// ou https://
        if (trimmedUrl.startsWith('http://') || trimmedUrl.startsWith('https://')) {
            return false;
        }

        // Deve ter pelo menos um ponto (ex: exemplo.com)
        if (!trimmedUrl.includes('.')) {
            return false;
        }

        // Regex para validar formato: dominio.com/path ou dominio.com.br/path
        const urlPattern = /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*(\/[^\s]*)?$/;
        return urlPattern.test(trimmedUrl);
    };


    const handleCreateSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.network.trim()) {
            toast({
                variant: 'destructive',
                title: 'Campo obrigatório',
                description: 'Por favor, selecione a Conta GAM.',
            });
            return;
        }

        if (!formData.url.trim()) {
            toast({
                variant: 'destructive',
                title: 'Campo obrigatório',
                description: 'Por favor, preencha a URL.',
            });
            return;
        }

        // Valida formato da URL (sem https://)
        if (!validateUrlFormat(formData.url)) {
            toast({
                variant: 'destructive',
                title: 'URL inválida',
                description: 'A URL não deve começar com http:// ou https://. Use o formato: exemplo.com.br/hello',
            });
            return;
        }

        createUrlMutation.mutate(formData);
    };

    const handleEditSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        if (!selectedUrl) return;

        const updateData: UpdateUrlInput = {};
        if (formData.url && formData.url !== selectedUrl.url) {
            // Valida formato da URL se foi alterada
            if (!validateUrlFormat(formData.url)) {
                toast({
                    variant: 'destructive',
                    title: 'URL inválida',
                    description: 'A URL não deve começar com http:// ou https://. Use o formato: exemplo.com.br/hello',
                });
                return;
            }

            updateData.url = formData.url;
        }
        if (formData.inventory_type && formData.inventory_type !== selectedUrl.inventory_type) {
            updateData.inventory_type = formData.inventory_type;
        }
        if (formData.brand_type && formData.brand_type !== selectedUrl.brand_type) {
            updateData.brand_type = formData.brand_type;
        }

        if (Object.keys(updateData).length === 0) {
            toast({
                variant: 'destructive',
                title: 'Nenhuma alteração',
                description: 'Por favor, altere pelo menos um campo.',
            });
            return;
        }

        updateUrlMutation.mutate({
            urlId: selectedUrl.id,
            data: updateData,
        });
    };

    const openEditDialog = (url: Url) => {
        setSelectedUrl(url);
        setFormData({
            network: url.network,
            url: url.url,
            inventory_type: url.inventory_type,
            brand_type: url.brand_type,
        });
        setIsEditDialogOpen(true);
    };

    const openDeleteDialog = (url: Url) => {
        setSelectedUrl(url);
        setIsDeleteDialogOpen(true);
    };

    const handleDelete = () => {
        if (!selectedUrl) return;
        deleteUrlMutation.mutate(selectedUrl.id);
    };

    const getStatusBadge = (status: Url['status']) => {
        const statusConfig = {
            pending: { label: 'Pendente', className: 'bg-yellow-100 text-yellow-800 border-yellow-300' },
            processing: { label: 'Processando', className: 'bg-blue-100 text-blue-800 border-blue-300' },
            completed: { label: 'Concluído', className: 'bg-green-100 text-green-800 border-green-300' },
            failed: { label: 'Falhou', className: 'bg-red-100 text-red-800 border-red-300' },
        };

        const config = statusConfig[status];
        return (
            <Badge variant="outline" className={config.className}>
                {config.label}
            </Badge>
        );
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900">URLs</h1>
                    <p className="mt-2 text-gray-600">Gerencie as URLs do Google Ad Manager.</p>
                </div>
                <div className="flex gap-2">
                    <Button 
                        onClick={() => setIsCreateDialogOpen(true)}
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                    >
                        <Plus className="mr-2 h-4 w-4" />
                        Nova URL
                    </Button>
                    <Button 
                        onClick={handleSync}
                        disabled={syncMutation.isPending}
                        variant="outline"
                        className="border-blue-600 text-blue-600 hover:bg-blue-50"
                    >
                        {syncMutation.isPending ? (
                            <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Sincronizando...
                            </>
                        ) : (
                            <>
                                <RefreshCw className="mr-2 h-4 w-4" />
                                Sync
                            </>
                        )}
                    </Button>
                </div>
            </div>

            {/* Loading State */}
            {isLoading && (
                <div className="rounded-lg bg-white p-6 shadow flex items-center justify-center">
                    <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                    <p className="ml-2 text-gray-600">Carregando URLs...</p>
                </div>
            )}

            {/* Error State */}
            {error && (
                <div className="rounded-lg bg-red-50 border border-red-200 p-6">
                    <p className="text-red-800">Erro ao carregar URLs. Tente novamente.</p>
                </div>
            )}

            {/* URLs Table */}
            {urls && urls.length > 0 && (
                <div className="rounded-lg bg-white shadow border border-gray-100 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Conta GAM
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        URL
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Tipo de Inventário
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Tipo de Marca
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Status
                                    </th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Ações
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {urls.map((url) => (
                                    <tr key={url.id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <p className="text-sm font-medium text-gray-900">{formatConta(url.network)}</p>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-2">
                                                <p className="text-sm text-gray-900 max-w-md truncate">{url.url}</p>
                                                <a
                                                    href={`https://${url.url}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-blue-600 hover:text-blue-800"
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    <ExternalLink className="h-4 w-4" />
                                                </a>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <Badge variant="outline">{url.inventory_type}</Badge>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <Badge variant="outline">{url.brand_type}</Badge>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            {getStatusBadge(url.status)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right">
                                            <div className="flex justify-end gap-2">
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => openEditDialog(url)}
                                                >
                                                    <Pencil className="h-4 w-4" />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => openDeleteDialog(url)}
                                                >
                                                    <Trash2 className="h-4 w-4 text-red-600" />
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Empty State */}
            {urls && urls.length === 0 && (
                <div className="rounded-lg bg-white p-12 shadow border border-gray-100 text-center">
                    <ExternalLink className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhuma URL cadastrada</h3>
                    <p className="text-gray-600 mb-4">Comece criando sua primeira URL.</p>
                    <Button onClick={() => setIsCreateDialogOpen(true)}>
                        <Plus className="mr-2 h-4 w-4" />
                        Nova URL
                    </Button>
                </div>
            )}

            {/* Create URL Dialog */}
            <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>Criar Nova URL</DialogTitle>
                        <DialogDescription>
                            Adicione uma nova URL para ser criada no Google Ad Manager.
                        </DialogDescription>
                    </DialogHeader>

                    <form onSubmit={handleCreateSubmit} className="space-y-4 mt-4">
                        <div className="space-y-2">
                            <Label htmlFor="network">Conta GAM *</Label>
                            <Select
                                value={formData.network || undefined}
                                onValueChange={(value) =>
                                    setFormData({ ...formData, network: value })
                                }
                                disabled={createUrlMutation.isPending}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Selecione a Conta GAM" />
                                </SelectTrigger>
                                <SelectContent>
                                    {networks.map((network) => (
                                        <SelectItem key={network.network_code} value={network.network_code}>
                                            {network.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="url">URL *</Label>
                            <Input
                                id="url"
                                type="text"
                                placeholder="exemplo.com.br/hello"
                                value={formData.url}
                                onChange={(e) =>
                                    setFormData({ ...formData, url: e.target.value.trim() })
                                }
                                disabled={createUrlMutation.isPending}
                                required
                            />
                            <p className="text-xs text-gray-500">
                                Digite a URL sem https://. Exemplo: exemplo.com.br/hello
                            </p>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="inventory_type">Tipo de Inventário *</Label>
                                <Select
                                    value={formData.inventory_type}
                                    onValueChange={(value: 'Display' | 'Vídeo in-stream') =>
                                        setFormData({ ...formData, inventory_type: value })
                                    }
                                    disabled={createUrlMutation.isPending}
                                >
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="Display">Display</SelectItem>
                                        <SelectItem value="Vídeo in-stream">Vídeo in-stream</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="brand_type">Tipo de Marca *</Label>
                                <Select
                                    value={formData.brand_type}
                                    onValueChange={(value: 'Com marca' | 'semitransparente') =>
                                        setFormData({ ...formData, brand_type: value })
                                    }
                                    disabled={createUrlMutation.isPending}
                                >
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="Com marca">Com marca</SelectItem>
                                        <SelectItem value="semitransparente">Semitransparente</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        <div className="flex gap-3 pt-4">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => {
                                    setIsCreateDialogOpen(false);
                                    resetForm();
                                }}
                                disabled={createUrlMutation.isPending}
                                className="flex-1"
                            >
                                Cancelar
                            </Button>
                            <Button type="submit" disabled={createUrlMutation.isPending} className="flex-1">
                                {createUrlMutation.isPending ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Criando...
                                    </>
                                ) : (
                                    'Criar URL'
                                )}
                            </Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Edit URL Dialog */}
            <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>Editar URL</DialogTitle>
                        <DialogDescription>
                            Atualize as informações da URL.
                        </DialogDescription>
                    </DialogHeader>

                    <form onSubmit={handleEditSubmit} className="space-y-4 mt-4">
                        <div className="space-y-2">
                            <Label htmlFor="edit-url">URL</Label>
                            <Input
                                id="edit-url"
                                type="text"
                                placeholder="exemplo.com.br/hello"
                                value={formData.url}
                                onChange={(e) =>
                                    setFormData({ ...formData, url: e.target.value.trim() })
                                }
                                disabled={updateUrlMutation.isPending}
                            />
                            <p className="text-xs text-gray-500">
                                Digite a URL sem https://. Exemplo: exemplo.com.br/hello
                            </p>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="edit-inventory_type">Tipo de Inventário</Label>
                                <Select
                                    value={formData.inventory_type}
                                    onValueChange={(value: 'Display' | 'Vídeo in-stream') =>
                                        setFormData({ ...formData, inventory_type: value })
                                    }
                                    disabled={updateUrlMutation.isPending}
                                >
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="Display">Display</SelectItem>
                                        <SelectItem value="Vídeo in-stream">Vídeo in-stream</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="edit-brand_type">Tipo de Marca</Label>
                                <Select
                                    value={formData.brand_type}
                                    onValueChange={(value: 'Com marca' | 'semitransparente') =>
                                        setFormData({ ...formData, brand_type: value })
                                    }
                                    disabled={updateUrlMutation.isPending}
                                >
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="Com marca">Com marca</SelectItem>
                                        <SelectItem value="semitransparente">Semitransparente</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        <div className="flex gap-3 pt-4">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => {
                                    setIsEditDialogOpen(false);
                                    setSelectedUrl(null);
                                    resetForm();
                                }}
                                disabled={updateUrlMutation.isPending}
                                className="flex-1"
                            >
                                Cancelar
                            </Button>
                            <Button type="submit" disabled={updateUrlMutation.isPending} className="flex-1">
                                {updateUrlMutation.isPending ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Atualizando...
                                    </>
                                ) : (
                                    'Atualizar URL'
                                )}
                            </Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Delete URL Dialog */}
            <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Deletar URL</DialogTitle>
                        <DialogDescription>
                            Tem certeza que deseja deletar esta URL? Esta ação não pode ser desfeita.
                        </DialogDescription>
                    </DialogHeader>

                    {selectedUrl && (
                        <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                            <p className="text-sm text-gray-600">
                                <strong>Conta GAM:</strong> {formatConta(selectedUrl.network)}
                            </p>
                            <p className="text-sm text-gray-600">
                                <strong>URL:</strong> {selectedUrl.url}
                            </p>
                        </div>
                    )}

                    <div className="flex gap-3 pt-4">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => {
                                setIsDeleteDialogOpen(false);
                                setSelectedUrl(null);
                            }}
                            disabled={deleteUrlMutation.isPending}
                            className="flex-1"
                        >
                            Cancelar
                        </Button>
                        <Button
                            type="button"
                            variant="destructive"
                            onClick={handleDelete}
                            disabled={deleteUrlMutation.isPending}
                            className="flex-1"
                        >
                            {deleteUrlMutation.isPending ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Deletando...
                                </>
                            ) : (
                                'Deletar'
                            )}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}

